/**
 *
 * This module allows an open vue admin panel in iframe and pushes route with params
 *
 **/
import exoEventBus from '@modules/exoEventBus'
import exoFrame from '@/common/exoFrame/'
import exoInpage from '@/common/exoInpage/exoInpage'

// Routes whose admin views emit `exo-layout-view-ready` once their main
// component finishes mounting. For these we keep a high-zIndex overlay shown
// from the moment exoBridge.open() is called until the admin view inside the
// iframe is rendered — bridges the visible 3-4s admin SPA cold-boot.
const ROUTES_WITH_READY_EVENT = ['R_REACT_LAYOUT']

function _shouldShowOverlay(params) {
    return params && ROUTES_WITH_READY_EVENT.indexOf(params.name) !== -1
}

// Inject a CSS keyframes spinner once.
function _ensureLoaderStyles() {
    if (document.getElementById('exobridge-loader-styles')) return
    const css =
        '@keyframes exobridge-spin{to{transform:rotate(360deg)}}' +
        '.exobridge-loader{' +
            'position:fixed;inset:0;z-index:9999;' +
            'display:flex;align-items:center;justify-content:center;' +
            'background:rgba(15,15,15,0.55);' +
            'pointer-events:none;' +
        '}' +
        '.exobridge-loader__spinner{' +
            'width:56px;height:56px;border:4px solid rgba(255,255,255,0.25);' +
            'border-top-color:#fff;border-radius:50%;' +
            'animation:exobridge-spin 0.8s linear infinite;' +
        '}'
    const style = document.createElement('style')
    style.id = 'exobridge-loader-styles'
    style.textContent = css
    document.head.appendChild(style)
}

// Mount a CSS spinner on top of everything (z-index 9999) covering the
// viewport while the admin SPA inside the iframe cold-boots. We attach to
// document.body because attaching inside FrameBox can race with its
// `frame-box--preloading` visibility:hidden state.
function _showLoadingOverlay() {
    console.log('🌀 [exoBridge] showing loader')
    _ensureLoaderStyles()
    const el = document.createElement('div')
    el.className = 'exobridge-loader'
    el.innerHTML = '<div class="exobridge-loader__spinner"></div>'
    document.body.appendChild(el)

    let hidden = false
    const hide = (reason) => {
        if (hidden) return
        hidden = true
        console.log('🌀 [exoBridge] hiding loader, reason:', reason || 'manual')
        if (el.parentNode) el.parentNode.removeChild(el)
    }
    exoEventBus.$once('exo-layout-view-ready', () => hide('exo-layout-view-ready'))
    // Safety net so the user is never stuck staring at a spinner.
    const safety = setTimeout(() => hide('safety-12s'), 12000)
    return { hide: () => { clearTimeout(safety); hide('finally') } }
}

// How long open() waits for a preloaded bridge to report in before giving up
// on it. The admin SPA cold-boots in 1-4s and the preload starts when edit
// mode does, so by the time someone clicks it is normally long installed.
// When it is not - the frame never loaded, was detached, or its install event
// never reached this window (webkracht.nl, 16 Sep 2026: every admin modal hung
// and the browser never even requested the bridge page) - waiting forever
// shows the user nothing. Past this, the dead frame is dropped and the bridge
// is loaded fresh in the open modal, as it was before preloading existed.
const PRELOAD_WAIT_MS = 6000

let exoBridge = {
    _bridgeInstalled: false,
    _bridgeReady: null,

    /**
     * Pre-load the admin bridge iframe in background.
     * Call this early (e.g. on edit mode init) so the settings
     * dialog opens instantly when the user clicks the gear icon.
     */
    preload() {
        // With in-page modals on, that app boots first and the iframe after
        // it: both run the admin's whole start-up (some twenty API calls), and
        // side by side they only slow each other down - Pim's first in-page
        // open took 6-8 s with the two booting at once (17 Sep 2026).
        if (exoInpage.enabled() && !this._inpagePreloaded) {
            this._inpagePreloaded = true
            exoInpage.preload().then(() => this._preloadFrame(), () => this._preloadFrame())
            return
        }
        this._preloadFrame()
    },

    _preloadFrame() {
        if (this._bridgeInstalled || this._bridgeReady || exoFrame.has()) return
        this._bridgeReady = new Promise(resolve => {
            exoEventBus.$once('exo-bridge-installed', () => {
                this._bridgeInstalled = true
                resolve()
            })
        })
        exoFrame.preload({href: '/exo_admin/bridge'})
    },

    async open(params) {
        console.log('🌉 [exoBridge.open] called with', params, 'shouldShowOverlay=', _shouldShowOverlay(params))

        // Routes that have moved out of the iframe open in the page instead;
        // see exoInpage for the list and the switch. If that bundle does not
        // come up, this open still gets the iframe.
        if (exoInpage.handles(params)) {
            try {
                return await exoInpage.open(params)
            } catch (e) {
                console.warn('[exoBridge] in-page modal unavailable, using the iframe', e)
            }
        }

        const loader = _shouldShowOverlay(params) ? _showLoadingOverlay() : null

        try {
            if (this._bridgeInstalled && !exoFrame.has()) {
                // Installed once, but the frame is gone since - a hard close
                // (exolog-exo-modal-cancel) detaches it. reopen() on a detached
                // frame shows nothing and never resolves, so start over.
                this._bridgeInstalled = false
                this._bridgeReady = null
            }

            if (this._bridgeInstalled) {
                // Bridge already loaded — reuse iframe, just push new route
                this.push(params)
                return await exoFrame.reopen()
            }

            if (this._bridgeReady) {
                // Preload in progress — wait for it, then show. Bounded: see
                // PRELOAD_WAIT_MS.
                const installed = await Promise.race([
                    this._bridgeReady.then(() => true),
                    new Promise((resolve) => setTimeout(() => resolve(false), PRELOAD_WAIT_MS)),
                ])
                if (installed && exoFrame.has()) {
                    this.push(params)
                    return await exoFrame.reopen()
                }
                console.warn('[exoBridge] preloaded bridge did not come up within ' + PRELOAD_WAIT_MS + 'ms - loading it fresh')
                this._bridgeReady = null
                this._bridgeInstalled = false
                exoFrame.discard()
            }

            // No preload — load iframe now (original slow path)
            return await new Promise(resolve => {
                exoEventBus.$once('exo-bridge-installed', () => {
                    this._bridgeInstalled = true
                    this.push(params)
                })
                exoFrame.open({href: '/exo_admin/bridge'}).then(resolve)
            })
        } finally {
            // open() resolves when the modal is closed by the user. Hide the
            // overlay no matter what, in case it wasn't already hidden by the
            // exo-layout-view-ready event.
            if (loader) loader.hide()
        }
    },

    push(params) {
        //we set level 0 to push message only to child iframe
        //and prevent push to parent, in case when we have few level of iframe
        console.log('🌉 [EXOBRIDGE] push called with params:', params);
        console.log('🌉 [EXOBRIDGE] params type:', typeof params, 'constructor:', params?.constructor?.name);

        // Ensure params are serializable - remove any DOM references
        let cleanParams;
        try {
            cleanParams = JSON.parse(JSON.stringify(params));
            console.log('🌉 [EXOBRIDGE] clean params:', cleanParams);
        } catch (e) {
            console.error('🌉 [EXOBRIDGE] Failed to serialize params:', e);
            cleanParams = params;
        }

        exoEventBus.$emit('exo-router-push', cleanParams, 0)
    },

    replace(params) {
        exoEventBus.$emit('exo-router-replace', params)
    },

    close() {
        // An in-page modal closes in the page; the admin's close button sends
        // the same 'exo-popup-header-close-outer' either way.
        if (exoInpage.isOpen()) {
            exoInpage.close()
            return
        }
        // Use softClose to keep iframe alive for instant reopen
        if (exoBridge._bridgeInstalled) {
            exoFrame.softClose()
        } else {
            exoFrame.close()
        }
    }
}

exoEventBus.$on('exo-popup-header-close-outer', () => exoBridge.close())

// Reachable from the console, so "the modal does nothing" can be looked at
// where it happens: exoBridge._bridgeInstalled, exoBridge._bridgeReady.
window.exoBridge = exoBridge

export default exoBridge
